import { useEffect, useState } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router-dom"
import ApiHandler from "src/api/ApiHandler"
import ErrorHandler from "src/components/errors/ErrorHandler"
import Loader from "src/components/general/Loading"
import ValuationPage from "src/components/valuations/ValuationPage"
import ValuationLoader from "src/components/general/ValuationLoader"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import StateConstants from "src/states/StateContstants"
import PathRouter from "src/navigation/PathRouter"
import { usePostHog } from "@posthog/react"

interface ValuationV2PageProps {
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
    errorHandler: ErrorHandler
    loader: Loader
    loginHelper?: any
    featureProvider?: any
}

/**
 * Trial implementation using React Router v6 for the route:
 *   /valuate/:propertyId/:addressSlug
 * 
 * Retrieves address from backend, checks if URL matches the correct address_url,
 * and redirects if necessary before rendering ValuationPage.
 */
export default function ValuationPageServer(props: ValuationV2PageProps) {
    const { propertyId, addressSlug } = useParams<{
        propertyId: string
        addressSlug?: string
    }>()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const [isValidating, setIsValidating] = useState(true)
    const [shouldRender, setShouldRender] = useState(false)
    const posthog = usePostHog()

    useEffect(() => {
        // Parse query parameters for house condition and removed homes
        const houseCondition = searchParams.get(PathRouter.HOUSE_CONDITION_PARAM)
        const removedHomes = searchParams.get(PathRouter.REMOVED_HOMES_PARAM)

        if (houseCondition) {
            const parsedValue = parseFloat(houseCondition)
            if (!isNaN(parsedValue)) {
                const clampedValue = Math.min(Math.max(parsedValue, 0), 1)
                props.stateHandler.set(StateConstants.RELATIVE_CONDITION, clampedValue)
            }
        }

        if (removedHomes) {
            const homesArray = removedHomes.split(",").filter(h => h.length > 0)
            props.stateHandler.set(StateConstants.REMOVED_HOMES, homesArray)
        }

        async function validateAndRedirect() {
            if (!propertyId) {
                setIsValidating(false)
                return
            }

            try {
                const addressResponse = await props.apiHandler.getAddress({ "id": propertyId })
                const correctAddressUrl = addressResponse?.aurl || addressResponse?.address_url
                
                // If no slug in URL, redirect to URL with correct slug
                if (!addressSlug) {
                    if (correctAddressUrl) {
                        let correctUrl = `/valuate/${propertyId}/${encodeURIComponent(correctAddressUrl)}`
                        const queryParams = new URLSearchParams()
                        
                        if (houseCondition) {
                            queryParams.set(PathRouter.HOUSE_CONDITION_PARAM, houseCondition)
                        }
                        if (removedHomes) {
                            queryParams.set(PathRouter.REMOVED_HOMES_PARAM, removedHomes)
                        }
                        
                        const queryString = queryParams.toString()
                        if (queryString) {
                            correctUrl += `?${queryString}`
                        }
                        
                        navigate(correctUrl, { replace: true })
                        return
                    } else {
                        // No slug available, render without redirect
                        setIsValidating(false)
                        setShouldRender(true)
                        return
                    }
                }
                
                // If slug exists but doesn't match, redirect to correct slug
                if (correctAddressUrl && correctAddressUrl !== addressSlug) {
                    // Preserve query parameters when redirecting
                    let correctUrl = `/valuate/${propertyId}/${encodeURIComponent(correctAddressUrl)}`
                    const queryParams = new URLSearchParams()
                    
                    if (houseCondition) {
                        queryParams.set(PathRouter.HOUSE_CONDITION_PARAM, houseCondition)
                    }
                    if (removedHomes) {
                        queryParams.set(PathRouter.REMOVED_HOMES_PARAM, removedHomes)
                    }
                    
                    const queryString = queryParams.toString()
                    if (queryString) {
                        correctUrl += `?${queryString}`
                    }
                    
                    navigate(correctUrl, { replace: true })
                    return
                }

                // Slug matches or no slug available, render the page
                posthog?.capture('valuation_viewed', { property_id: propertyId, address_slug: addressSlug })
                setIsValidating(false)
                setShouldRender(true)
            } catch (error) {
                console.error("Error validating address URL:", error)
                props.stateHandler.set(StateConstants.ERROR, error)
                setIsValidating(false)
                setShouldRender(true)
            }
        }

        validateAndRedirect()
    }, [propertyId, addressSlug, navigate, props.apiHandler, searchParams, props.stateHandler])

    if (!propertyId) {
        return (
            <div>
                Missing property id. Try a URL like <code>/valuate/{`{propertyId}`}/{`{addressSlug}`}</code>
            </div>
        )
    }

    if (isValidating || !shouldRender) {
        return <ValuationLoader />
    }

    return (
        <ValuationPage
            propertyId={propertyId}
            stateHandler={props.stateHandler}
            apiHandler={props.apiHandler}
            errorHandler={props.errorHandler}
            loader={props.loader}
            loginHelper={props.loginHelper}
            featureProvider={props.featureProvider}
        />
    )
}

